import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, IdentificationCard, Eye, Key } from "@phosphor-icons/react";
import LicenceDialog from "../components/LicenceDialog";
import DepartmentAssign from "../components/DepartmentAssign";
import { useNavigate } from "react-router-dom";

const ALL_PERMS = [
  ["driver", "Driver"], ["maintenance", "Maintenance"], ["supervisor", "Supervisor"],
  ["administrator", "Administrator"], ["rams_manager", "RAMS Manager"], ["ppe_manager", "PPE Manager"],
  ["training_manager", "Training Manager"], ["asset_manager", "Asset Manager"], ["auditor", "Auditor"],
  ["return_to_service", "Return to Service"], ["verify_competencies", "Verify Competencies"],
  ["manage_user_auth", "Manage User Login"],
];

export default function PeoplePage() {
  const { hasPerm, enterViewAs } = useAuth();
  const nav = useNavigate();
  const [people, setPeople] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", employee_number: "", employment_type: "employee", permissions: [] });
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [editInfo, setEditInfo] = useState(null);
  const [infoForm, setInfoForm] = useState({ first_name: "", last_name: "", email: "", employee_number: "" });
  const [licencePerson, setLicencePerson] = useState(null);
  const [deptPerson, setDeptPerson] = useState(null);
  const [authPerson, setAuthPerson] = useState(null);
  const [tempResult, setTempResult] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);

  const load = useCallback(() => { api.get("/people").then((r) => setPeople(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (list, setList, code) => setList(list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);

  const invite = async () => {
    try {
      const r = await api.post("/people/invite", form);
      setResult(r.data);
      toast.success("User invited");
      setForm({ first_name: "", last_name: "", email: "", employee_number: "", employment_type: "employee", permissions: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const savePerms = async () => {
    try {
      await api.post(`/people/${editing.id}/permissions`, { permissions: editPerms });
      toast.success("Permissions updated"); setEditing(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  const saveInfo = async () => {
    try {
      await api.put(`/people/${editInfo.id}`, infoForm);
      toast.success("Details updated"); setEditInfo(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendReset = async () => {
    setAuthBusy(true);
    try {
      await api.post(`/people/${authPerson.id}/send-password-reset`);
      toast.success("Password recovery email sent");
      setAuthPerson(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAuthBusy(false); }
  };

  const issueTemp = async () => {
    setAuthBusy(true);
    try {
      const r = await api.post(`/people/${authPerson.id}/issue-temp-password`);
      setTempResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAuthBusy(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">People</h1>
        {hasPerm("administrator") && (
          <Button data-testid="invite-user-button" onClick={() => { setInviteOpen(true); setResult(null); }} className="bg-slate-900 hover:bg-slate-800 font-semibold">
            <UserPlus size={18} weight="bold" className="mr-1" /> Invite User
          </Button>
        )}
      </div>

      <div className="bg-white border-2 border-slate-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b-2 border-slate-200">
            <tr className="text-left font-body text-slate-600">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Emp #</th><th className="px-4 py-2">Licence</th><th className="px-4 py-2">Permissions</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} data-testid={`person-row-${p.id}`} className="border-b border-slate-100">
                <td className="px-4 py-2 font-semibold text-slate-800">{p.first_name} {p.last_name}</td>
                <td className="px-4 py-2 text-slate-600">{p.email}</td>
                <td className="px-4 py-2 font-mono text-slate-600">{p.employee_number || "—"}</td>
                <td className="px-4 py-2">
                  {p.licence?.has
                    ? <span data-testid={`licence-badge-${p.id}`} className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${p.licence.status === "valid" ? "bg-emerald-600 text-white" : p.licence.status ? "bg-amber-500 text-slate-900" : "bg-slate-300 text-slate-700"}`}>{p.licence.status || "held"} · {p.licence.entitlements} cat{p.licence.entitlements === 1 ? "" : "s"}</span>
                    : <span data-testid={`licence-badge-${p.id}`} className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-slate-200 text-slate-500">No licence</span>}
                </td>
                <td className="px-4 py-2"><div className="flex flex-wrap gap-1">{p.permissions.map((c) => <span key={c} className="text-xs bg-slate-200 rounded px-1.5 py-0.5">{c}</span>)}</div></td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    {hasPerm("administrator") && <button data-testid={`edit-info-${p.id}`} onClick={() => { setEditInfo(p); setInfoForm({ first_name: p.first_name || "", last_name: p.last_name || "", email: p.email || "", employee_number: p.employee_number || "" }); }} className="text-slate-700 font-semibold">Edit</button>}
                    {hasPerm("training_manager", "supervisor", "administrator") && <button data-testid={`licence-${p.id}`} onClick={() => setLicencePerson(p)} className="text-emerald-700 font-semibold inline-flex items-center gap-1"><IdentificationCard size={16} weight="bold" />Licence</button>}
                    {hasPerm("supervisor", "administrator") && <button data-testid={`view-as-${p.id}`} onClick={async () => { await enterViewAs(p); nav("/"); }} className="text-purple-700 font-semibold inline-flex items-center gap-1"><Eye size={16} weight="bold" />View as</button>}
                    {hasPerm("supervisor", "administrator") && <button data-testid={`dept-${p.id}`} onClick={() => setDeptPerson(p)} className="text-slate-700 font-semibold">Departments</button>}
                    {hasPerm("administrator") && <button data-testid={`edit-perms-${p.id}`} onClick={() => { setEditing(p); setEditPerms(p.permissions); }} className="text-blue-600 font-semibold">Permissions</button>}
                    {hasPerm("manage_user_auth") && p.has_login && <button data-testid={`reset-auth-${p.id}`} onClick={() => { setAuthPerson(p); setTempResult(null); }} className="text-amber-700 font-semibold inline-flex items-center gap-1"><Key size={16} weight="bold" />Reset password</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Invite User</DialogTitle></DialogHeader>
          {result ? (
            <div className="flex flex-col gap-3">
              <p className="font-body text-slate-700">User created. Share this temporary password securely — they'll change it on first login.</p>
              <div className="bg-slate-100 rounded p-3 font-mono text-sm break-all" data-testid="invite-temp-password">{result.email}<br />{result.temp_password}</div>
              <Button onClick={() => setInviteOpen(false)} className="bg-slate-900 font-semibold">Done</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="invite-first" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                <Input data-testid="invite-last" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <Input data-testid="invite-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="invite-empnum" placeholder="Employee number (optional)" value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} />
              <div>
                <p className="font-body text-sm font-semibold text-slate-700 mb-1">Permissions</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {ALL_PERMS.map(([code, label]) => (
                    <label key={code} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" data-testid={`invite-perm-${code}`} checked={form.permissions.includes(code)}
                        onChange={() => toggle(form.permissions, (v) => setForm({ ...form, permissions: v }), code)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="invite-submit" onClick={invite} disabled={!form.email || !form.first_name} className="bg-blue-600 hover:bg-blue-700 font-semibold">Create & Invite</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit permissions */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Permissions — {editing?.first_name} {editing?.last_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-1.5">
            {ALL_PERMS.map(([code, label]) => (
              <label key={code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`editperm-${code}`} checked={editPerms.includes(code)} onChange={() => toggle(editPerms, setEditPerms, code)} />
                {label}
              </label>
            ))}
          </div>
          <Button data-testid="save-perms" onClick={savePerms} className="bg-slate-900 font-semibold">Save</Button>
        </DialogContent>
      </Dialog>

      {licencePerson && <LicenceDialog person={licencePerson} onClose={() => setLicencePerson(null)} />}

      {/* Reset / password recovery */}
      <Dialog open={!!authPerson} onOpenChange={(o) => !o && (setAuthPerson(null), setTempResult(null))}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Reset password — {authPerson?.first_name} {authPerson?.last_name}</DialogTitle>
            <DialogDescription>Send a self-service recovery email (recommended) or issue a one-time temporary password.</DialogDescription>
          </DialogHeader>
          {tempResult ? (
            <div className="flex flex-col gap-3" data-testid="temp-password-result">
              <p className="font-body text-sm text-slate-700">Temporary password issued. Share it securely — it will
                <strong> not be shown again</strong>. {authPerson?.first_name} must change it at next login.</p>
              <div className="bg-slate-100 rounded p-3 font-mono text-sm break-all" data-testid="temp-password-value">
                {tempResult.email}<br />{tempResult.temp_password}
              </div>
              <Button data-testid="temp-password-done" onClick={() => { setAuthPerson(null); setTempResult(null); }} className="bg-slate-900 font-semibold">Done</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2">
                <p className="font-body text-sm font-semibold text-slate-800">Send password recovery email</p>
                <p className="font-body text-xs text-slate-500">Emails {authPerson?.email} a secure link to set their own new password. No password is created or shown.</p>
                <Button data-testid="send-reset-email" onClick={sendReset} disabled={authBusy} className="bg-blue-600 hover:bg-blue-700 font-semibold">Send recovery email</Button>
              </div>
              <div className="border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2">
                <p className="font-body text-sm font-semibold text-slate-800">Issue temporary password</p>
                <p className="font-body text-xs text-slate-500">Generates a one-time password (shown once) that forces a change at next login. Use only when email is not possible.</p>
                <Button data-testid="issue-temp-password" onClick={issueTemp} disabled={authBusy} variant="outline" className="border-2 font-semibold">Issue temporary password</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Departments */}
      <Dialog open={!!deptPerson} onOpenChange={(o) => !o && setDeptPerson(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Departments — {deptPerson?.first_name} {deptPerson?.last_name}</DialogTitle>
            <DialogDescription>Choose which departments this person belongs to. Leave empty for a floating employee who works across the whole company.</DialogDescription>
          </DialogHeader>
          {deptPerson && <DepartmentAssign entityType="profile" entityId={deptPerson.id} label={`${deptPerson.first_name} ${deptPerson.last_name}`} />}
        </DialogContent>
      </Dialog>

      {/* Edit person details */}
      <Dialog open={!!editInfo} onOpenChange={(o) => !o && setEditInfo(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Edit Details — {editInfo?.first_name} {editInfo?.last_name}</DialogTitle>
            <DialogDescription>Update name, login email and employee number.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-slate-600">First name</label>
                <Input data-testid="edit-first" value={infoForm.first_name} onChange={(e) => setInfoForm({ ...infoForm, first_name: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Last name</label>
                <Input data-testid="edit-last" value={infoForm.last_name} onChange={(e) => setInfoForm({ ...infoForm, last_name: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Email (login address)</label>
              <Input data-testid="edit-email" value={infoForm.email} onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Employee number</label>
              <Input data-testid="edit-empnum" value={infoForm.employee_number} onChange={(e) => setInfoForm({ ...infoForm, employee_number: e.target.value })} /></div>
            <p className="text-xs text-slate-500">Changing the email also updates the person's sign-in address.</p>
            <Button data-testid="edit-info-save" onClick={saveInfo} disabled={!infoForm.first_name || !infoForm.email} className="bg-slate-900 font-semibold">Save Details</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
